/*
 * Actions for the [measurements store]{link module:store/measurements}
 *
 * @module store/measurements/actions
 */

import * as apiMeasurements from '@/api/measurements'
import {
  Device,
  endOfYearMoment,
  Sensor,
  SENSOR_FLAG_VALUES,
  SENSOR_KINDS,
  startOfYearMoment,
  TIMEOUTS_IN_MS
} from '@/store/modules/measurements'
import moment from 'moment'
import Papa from 'papaparse'

export async function getSensorsEnergyMeasurements(
  { getters, commit },
  { ctxt = 'customer', sensorIds = null, type = 'historical_energy', emsIds = null, tFilter = {} }
) {
  const kind = createKindFromType(type)

  if (!sensorIds && !emsIds) {
    sensorIds = getters['sensorIds']
  }

  const filter = {
    t_filter: tFilter
  }

  if (kind && emsIds && emsIds.length > 0) {
    filter.emsIds = emsIds.join(',')
    filter.kind = kind
  }

  if (!emsIds && !kind && sensorIds && sensorIds.length > 0) {
    filter.sensorIds = sensorIds.join(',')
  }

  const rsp = await apiMeasurements.getSensorsEnergyMeasurements({
    ctxt,
    filter
  })
  commit('SET_SENSOR_VALS', rsp.payload.data[0].attributes)
}

export async function getSensorsMetaData(
  { commit, rootGetters },
  {
    ctxt = 'customer',
    emsIds = [],
    isMeter = undefined,
    sensorTypeId = undefined,
    hasAnyClassification = undefined,
    hasAllClassifications = undefined,
    type = 'historical_energy',
    prepareVals = true,
    filterSensorsForDashboard = false
  }
) {
  const kind = createKindFromType(type)

  const filter = {
    kind
  }

  if (emsIds && emsIds.length > 0) {
    filter.emsIds = emsIds.join(',')
  }

  let sensorTypeFilter = {
    isMeter,
    sensorTypeId,
    hasAnyClassification,
    hasAllClassifications
  }
  sensorTypeFilter = Object.entries(sensorTypeFilter).filter((x) => x[1] !== undefined)
  if (sensorTypeFilter.length) {
    filter.sensorType = Object.fromEntries(sensorTypeFilter)
  }

  const locale = rootGetters['user/locale']

  const rsp = await apiMeasurements.getSensorsMetaData({
    ctxt,
    filter,
    locale
  })

  const parsedSensors = parseSensorMetadata(rsp.payload)

  commit('SET_SENSORS', { sensors: parsedSensors, prepareVals, filterSensorsForDashboard })
  commit('SET_SENSORS_HAS_PERMISSION_RESTRICTION', rsp.payload.meta?.hasPermissionRestriction)
  commit(
    'FIX_SENSOR_REFS',
    Object.values(parsedSensors).filter((sensor) => sensor.refId)
  )
}

export async function getDeviceMetaData({ commit, rootGetters }, { ctxt = 'customer', emsIds = [] }) {
  const filter = {}

  if (emsIds && emsIds.length > 0) {
    filter.emsIds = emsIds.join(',')
  }
  const locale = rootGetters['user/locale']

  const rsp = await apiMeasurements.getDeviceMetaData({
    ctxt,
    filter,
    locale
  })

  commit('SET_DEVICES', parseDeviceMetadata(rsp.payload))
}

function createKindFromType(type) {
  if (!type) {
    return 0
  }
  switch (type) {
    case 'all_sensors':
      return SENSOR_KINDS.MEASURED_QUANTITY_IN_LABEL | SENSOR_KINDS.NON_INSTANT | SENSOR_KINDS.NAKED_LABEL
    case 'historical_energy':
      return SENSOR_KINDS.METER | SENSOR_KINDS.PAST | SENSOR_KINDS.ENERGY_LABEL
    case 'instant_update':
      return SENSOR_KINDS.INSTANT
    case 'meter':
      return SENSOR_KINDS.MEASURED_QUANTITY_IN_LABEL | SENSOR_KINDS.METER | SENSOR_KINDS.NAKED_LABEL
    case 'past':
      return SENSOR_KINDS.MEASURED_QUANTITY_IN_LABEL | SENSOR_KINDS.PAST | SENSOR_KINDS.NAKED_LABEL
  }
}

export function parseSensorMetadata(payload) {
  const devices = {}
  const sensorTypes = {}

  //parse included
  if (Array.isArray(payload.included)) {
    for (const included of payload.included) {
      if (included.type === 'devices') {
        devices[included.id] = included.attributes
      }
      if (included.type === 'sensor_types') {
        sensorTypes[included.id] = included.attributes
      }
    }
  }

  const sensors = {}
  for (const sensor of payload.data) {
    const apiKey = sensor.attributes.apiKey
    const deviceId = sensor?.relationships?.device?.data?.id
    const device = devices[deviceId]
    const sensorType = sensorTypes[sensor.relationships.sensorType?.data?.id] || sensor.attributes

    sensors[apiKey] = new Sensor({
      id: sensor.id,
      compositeId: sensor.attributes?.compositeId,
      sid: sensor.attributes?.sid,
      emsId: sensor.attributes?.emsId,
      countId: sensor.attributes?.countId,
      label: sensor.attributes?.label,
      countNum: sensor.attributes?.countNum,
      flag: sensor.attributes?.flag,
      apiKey,
      createdAt: sensor.attributes?.createdAt,
      unit: sensor.attributes?.unit,
      deviceId,
      deviceType: device?.deviceType,
      deviceLabel: device?.label,
      sensorTypeFlag: sensorType?.flag,
      sensorTypeId: sensorType?.typeId,
      isMeter: sensorType?.isMeter,
      refId: sensor.relationships?.sensor?.data?.id,
      type: sensor.type
    })
  }

  return sensors
}

export function parseDeviceMetadata(payload) {
  const devices = {}

  for (const device of payload.data) {
    const children = []
    if (device.relationships.children) {
      for (const child of device.relationships.children.data) {
        children.push(parseInt(child.id))
      }
    }

    if (device.relationships.devices) {
      for (const child of device.relationships.devices.data) {
        children.push(parseInt(child.id))
      }
    }

    const sensors = []
    if (device.relationships.sensors) {
      for (const sensor of device.relationships.sensors.data) {
        sensors.push(sensor.id)
      }
    }
    if (device.relationships.statusSensors) {
      for (const sensor of device.relationships.statusSensors.data) {
        sensors.push(sensor.id)
      }
    }

    devices[device.id] = new Device({
      id: device.id,
      deviceType: device.attributes.deviceType,
      countId: device.attributes.emsCountId,
      emsId: device.attributes.energyManagementSystemId,
      flag: device.attributes.flag,
      givenName: device.attributes.givenName,
      iecLdn: device.attributes.iecLdn,
      iecNodePostfix: device.attributes.iecNodePostfix,
      iecNodePrefix: device.attributes.iecNodePrefix,
      label: device.attributes.label,
      defaultLabel: device.attributes.defaultLabel,
      children,
      sensors
    })
  }

  return devices
}

/**
 * Function to start a FE download
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the call
 * @param {string}Array params.sensorIds are the desired Ids of the Sensors
 * @param {sting} params.sequenceKind is the datatype ('avg', 'power', 'energy', 'bare_meter')
 * @param {integer} params.countNum is the number of Sensors with this 'emsId.sensorTypeId' combination
 * @param {integer} params.flag is the kind of the sensor 'e.g instant, ref...'
 * @param {object} params.tFilter chosen timefilter
 * @param {string} params.resolution picked time resolution ('minute',...)
 * @param {string} params.tz the timezone for the datacall
 * @param {string} params.header the headerlabel (HUMANIZED || ID)
 * @param {integer} params.preview wanted header(positive)||tail(negative) preview length
 *
 */
export async function downloadMeasurements(
  { commit },
  {
    ctxt = 'customer',
    sensorIds = null,
    sequenceKind = 'energy',
    tFilter = {},
    resolution = null,
    tz = 'utc',
    header = null,
    preview = null,
    downloadId = null,
    colSep = null,
    decSep = null
  }
) {
  const path = apiMeasurements.getDownloadPath({
    ctxt,
    sensorIds,
    sequenceKind,
    tFilter,
    resolution,
    tz,
    header,
    preview,
    downloadId,
    colSep,
    decSep
  })

  if (preview) {
    return new Promise((resolve, reject) => {
      Papa.parse(path, {
        download: true,
        header: true,
        //worker: true,
        chunk: function (results) {
          commit('SAVE_STREAM', { data: results.data, addFiller: preview > 0 })
        },
        complete: function () {
          resolve()
        },
        error: function (err) {
          reject(err)
        }
      })
    })
  }
}

export async function getMonthlyCounterMeasurementsForYear(
  { commit },
  { ctxt = 'customer', sensorIds = [], year = moment().year() }
) {
  const filter = {
    sensorIds: sensorIds.join(','),
    tFilter: {
      dateFrom: startOfYearMoment(year).format(),
      dateTo: endOfYearMoment(year).format()
    }
  }

  const rsp = await apiMeasurements.getMonthlyCounterMeasurements({
    ctxt,
    filter
  })

  for (const sensorMms of rsp.payload.data) {
    commit('ADD_SENSOR_SERIES', {
      sensorId: sensorMms.id,
      ts: sensorMms.attributes.datetimes,
      unit: sensorMms.attributes.unit,
      values: sensorMms.attributes.values
    })
  }
}

/**
 * Function to start Value Tracking
 *
 * Every TIMEOUTS_IN_MS.checkFrequency, commits a reset of older values
 *
 */
export function startInstantUpdateValuesRecencyTracker({ commit }) {
  return window.setInterval(() => {
    commit('CLEAR_OLD_VALS')
  }, TIMEOUTS_IN_MS.checkFrequency)
}

export function resetStore({ commit }) {
  commit('RESET_STORE')
}

export function getMeasurementSequences(
  { commit, state },
  { ctxt = 'customer', sensorIds = [], tFilter = {}, resolution = null, type = undefined }
) {
  if (!sensorIds.length)
    sensorIds = Object.values(state.sensors)
      .filter((s) => s.type === 'sensors' && (s.flag & SENSOR_FLAG_VALUES.instantUpdate) === 0)
      .map((s) => s.id)

  return apiMeasurements.getMeasurementSequences({ ctxt, sensorIds, resolution, tFilter, type }).then((rsp) => {
    const seqs = rsp?.payload?.data?.attributes

    if (!seqs) return

    return commit('SET_SENSOR_SEQS', seqs)
  })
}

export async function getStatusSensorsMetaData({ commit }, { ctxt = 'customer', emsIds = [] }) {
  const filter = {}

  if (emsIds && emsIds.length > 0) {
    filter.emsIds = emsIds.join(',')
  }

  const rsp = await apiMeasurements.getStatusSensorsMetaData({
    ctxt,
    filter
  })

  const parsedSensors = parseSensorMetadata(rsp.payload)

  commit('SET_SENSORS', { sensors: parsedSensors })
  commit('SET_SENSORS_HAS_PERMISSION_RESTRICTION', rsp.payload.meta?.hasPermissionRestriction)
}
